// @ts-strict-ignore
import type { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import type { EngagementActionsData } from '@empathybroker/tsclient-stats/dist/models/engagement/actions/engagement-actions-data'
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type { ActionContext, Store } from 'vuex'
import type {
  ActionClass,
  DateFilterSelectedValues,
  FeatureNode,
  FeaturesTree,
  TaggingActionData,
  TaggingActionDataItem,
  TaggingPayload,
  TaggingState,
} from '../../../models'
import type { FeatureConfigProvider } from '../../../providers/interfaces/feature-config.provider'

import { EngagementActionsTypeEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-actions-type.enum'

import { EngagementTypeComponentEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-type-component.enum'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'

import { CONTAINER } from '../../../config/inversify.config'

import { TYPES } from '../../../config/inversify.types'

import { FeatureIdEnum, TaggingTypeEnum } from '../../../enums'

import { TaggingUtils } from '../../../utils'
import { ANALYTICS_CONSTS } from '../../analytics/analytics.consts'
import { APP_CONSTS } from '../../app/app.consts'
import { TAGGING_CONSTS } from '../tagging.consts'

export class LoadActionsAction implements ActionClass {
  static async execute(
    this: void,
    { commit, rootGetters }: ActionContext<TaggingState, any>,
    payload: DateFilterSelectedValues,
  ): Promise<void> {
    const featuresTree: FeaturesTree =
      rootGetters[`${APP_CONSTS.moduleName}/${APP_CONSTS.getters.featuresTree}`]
    const data = await LoadActionsAction.getData(featuresTree, payload)
    commit(TAGGING_CONSTS.mutations.setData, {
      data,
      type: TaggingTypeEnum.ACTION,
    })
  }

  private static async getData(
    featuresTree: FeaturesTree,
    period: DateFilterSelectedValues,
  ): Promise<TaggingActionData> {
    const featuresTreeCopy = TaggingUtils.removeBrowseAnalyticsFromFeaturesTree(featuresTree)
    return Object.entries(featuresTreeCopy).reduce(
      async (
        dataPromise: Promise<TaggingActionData>,
        [domain, nodes]: [DomainEnum, FeatureNode[]],
      ) => {
        const data = await dataPromise
        const domainData = await LoadActionsAction.getDomainData(domain, nodes, data, period)
        data.push(...domainData)
        return data
      },
      Promise.resolve([]),
    )
  }

  private static async getDomainData(
    domain: DomainEnum,
    nodes: FeatureNode[],
    data: TaggingActionData,
    period: DateFilterSelectedValues,
  ): Promise<TaggingActionData> {
    return nodes.reduce(
      async (domainDataPromise: Promise<TaggingActionData>, node: FeatureNode) => {
        const domainData = await domainDataPromise
        const geFeatureConfigProvider = CONTAINER.get<FeatureConfigProvider>(
          TYPES.FeatureConfigProvider,
        )
        const { taggingConfig } = geFeatureConfigProvider(node.id as FeatureIdEnum, { domain })
        if (taggingConfig.id) {
          const dataItemRepeated = data.find(
            (dataItem: TaggingActionDataItem) => dataItem.id === taggingConfig.id,
          )
          if (dataItemRepeated) {
            const domainDataItem = await LoadActionsAction.getDomainData(
              domain,
              node.children,
              data,
              period,
            )
            dataItemRepeated.children.push(...domainDataItem)
          } else {
            const hasChildren = !!node.children?.length
            const dataItem = {
              domain,
              id: taggingConfig.id,
              nodeId: node.id as FeatureIdEnum,
              ...(!hasChildren && { context: taggingConfig.context }),
              types: this.getTypesById(),
              children: [],
            }
            const payload = TaggingUtils.getTaggingPayload(node.id as FeatureIdEnum, domain)
            if (hasChildren) {
              dataItem.children = await LoadActionsAction.getDomainData(
                domain,
                node.children,
                data,
                period,
              )
              Object.keys(dataItem.types).forEach((actionType: EngagementActionsTypeEnum) => {
                dataItem.types[actionType] = dataItem.children.reduce(
                  (result: number, childDataItem: TaggingActionDataItem) => {
                    const actionCount: number = childDataItem.types[actionType]
                    return result + actionCount
                  },
                  0,
                )
              })
            } else if (!!payload && !!period) {
              const actionTypes = Object.keys(dataItem.types) as EngagementActionsTypeEnum[]
              try {
                if (node.id === FeatureIdEnum.EXPLAIN) {
                  const dataActions = await this.getExplainData(payload, period)
                  dataItem.types = {
                    ...dataItem.types,
                    [EngagementActionsTypeEnum.SEARCH]: dataActions.searchCount,
                    [EngagementTypeComponentEnum.DOWNLOAD]: dataActions.downloadCount,
                  }
                } else {
                  const dataActions = await LoadActionsAction.fetchData(
                    payload,
                    period,
                    actionTypes,
                    [],
                  )
                  actionTypes.forEach((actionType: EngagementActionsTypeEnum) => {
                    dataItem.types[actionType] = {
                      [EngagementActionsTypeEnum.CREATE]: dataActions.createCount,
                      [EngagementActionsTypeEnum.EDIT]: dataActions.editCount,
                      [EngagementActionsTypeEnum.DELETE]: dataActions.deleteCount,
                      [EngagementActionsTypeEnum.SEARCH]: 0,
                      [EngagementTypeComponentEnum.DOWNLOAD]: 0,
                    }[actionType]
                  })
                }
              } catch (error) {
                console.error(error)
              }
            }
            domainData.push(dataItem)
          }
        }
        return domainData
      },
      Promise.resolve([]),
    )
  }

  private static getTypesById(): Partial<
    Record<EngagementActionsTypeEnum | EngagementTypeComponentEnum, number>
  > {
    return {
      [EngagementActionsTypeEnum.CREATE]: 0,
      [EngagementActionsTypeEnum.EDIT]: 0,
      [EngagementActionsTypeEnum.DELETE]: 0,
    }
  }

  private static async getExplainData(
    payload: TaggingPayload,
    period: DateFilterSelectedValues,
  ): Promise<Pick<EngagementActionsData, 'searchCount' | 'downloadCount'>> {
    try {
      const [downloadData, searchData] = await Promise.all([
        LoadActionsAction.fetchData(
          payload,
          period,
          [EngagementActionsTypeEnum.CLICK],
          [EngagementTypeComponentEnum.DOWNLOAD],
        ),
        LoadActionsAction.fetchData(payload, period, [EngagementActionsTypeEnum.SEARCH], []),
      ])

      return {
        downloadCount: downloadData.downloadCount,
        searchCount: searchData.searchCount,
      }
    } catch (error) {
      throw error
    }
  }

  private static async fetchData(
    payload: TaggingPayload,
    period: DateFilterSelectedValues,
    types: EngagementActionsTypeEnum[],
    components: EngagementTypeComponentEnum[],
  ): Promise<EngagementActionsData> {
    const store = CONTAINER.get<Store<any>>(TYPES.Store)
    const engagementActionsResponse: StatsResponse = await store.dispatch(
      `${ANALYTICS_CONSTS.moduleName}/${ANALYTICS_CONSTS.actions.fetchStatsReport}`,
      {
        id: StatsReportsIdsEnum.ENGAGEMENT_ACTIONS,
        params: {
          startDate: new Date(period[0]),
          endDate: new Date(period[1]),
          types,
          ...(components.length && { components }),
          ...payload,
        },
      },
      { root: true },
    )
    return engagementActionsResponse.data
  }
}
